/**
 * hk_os · <VoicePlayer>
 * ────────────────────────────────────────────────────────────────────
 * Universal voice player. Given a snippet `voiceKey`, plays the OA
 * audio (cached-first), exposes replay + progress + duration.
 *
 * Props:
 *   voiceKey    — required snippet key (e.g. "VOICE_NEXT_TASK")
 *   autoPlay    — play once on mount (default false)
 *   size        — "sm" | "md" | "lg" (default "md")
 *   showText    — render the Bengali text alongside the icon (from OA)
 *   onEnded     — fires when playback completes
 *   testid
 *
 * Constitutional: no Bengali hardcoded here; text comes from OA.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play, RotateCcw, Volume2, VolumeX, WifiOff } from "lucide-react";
import { useVoiceRegistry } from "./useVoiceRegistry";

const SIZE = {
  sm: { btn: "h-10 w-10", icon: "w-4 h-4", text: "text-[13px]" },
  md: { btn: "h-12 w-12", icon: "w-5 h-5", text: "text-[15px]" },
  lg: { btn: "h-16 w-16", icon: "w-7 h-7", text: "text-[18px]" },
};

export function VoicePlayer({
  voiceKey,
  autoPlay = false,
  size = "md",
  showText = true,
  onEnded,
  testid,
}) {
  const { getSnippet, play, stop, isOffline, isReady } = useVoiceRegistry();
  const snippet = getSnippet(voiceKey);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const hasAutoPlayed = useRef(false);
  const sz = SIZE[size] || SIZE.md;

  const handlePlay = useCallback(async () => {
    if (!snippet?.audio_url) return;
    setPlaying(true);
    setProgress(0);
    const res = await play(voiceKey, {
      onProgress: (p) => setProgress(Math.min(1, Math.max(0, p))),
    });
    setPlaying(false);
    setProgress(res.played ? 1 : 0);
    if (res.reason === "autoplay_blocked") setAutoplayBlocked(true);
    if (res.played) onEnded?.();
  }, [voiceKey, snippet, play, onEnded]);

  useEffect(() => {
    if (autoPlay && !hasAutoPlayed.current && snippet && isReady) {
      hasAutoPlayed.current = true;
      handlePlay();
    }
  }, [autoPlay, snippet, isReady, handlePlay]);

  useEffect(() => () => stop(), [stop]);

  const hasAudio = !!snippet?.audio_url;
  const Icon = !hasAudio ? VolumeX : playing ? Volume2 : progress > 0 ? RotateCcw : Play;
  const label = playing ? "Playing" : progress > 0 ? "Replay" : hasAudio ? "Play" : "No audio";

  return (
    <div
      data-testid={testid || `voice-player-${voiceKey}`}
      className="inline-flex items-center gap-3"
    >
      <button
        type="button"
        onClick={handlePlay}
        disabled={!hasAudio || playing}
        aria-label={label}
        data-testid={testid ? `${testid}-btn` : `voice-player-${voiceKey}-btn`}
        className={[
          "relative shrink-0 rounded-full border-2 flex items-center justify-center",
          "transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300",
          sz.btn,
          hasAudio ? "border-sky-600 bg-sky-50 text-sky-700 hover:bg-sky-100"
                     : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed",
          playing ? "animate-pulse" : "",
        ].join(" ")}
      >
        <Icon className={sz.icon} strokeWidth={2.4} />
        {/* Progress ring */}
        {playing && (
          <svg className="absolute inset-0 w-full h-full -rotate-90"
                viewBox="0 0 40 40" aria-hidden>
            <circle cx="20" cy="20" r="18"
                     stroke="rgb(2 132 199)" strokeWidth="2"
                     fill="none"
                     strokeDasharray={`${progress * 113} 113`}
                     strokeLinecap="round" />
          </svg>
        )}
        {isOffline && hasAudio && (
          <span className="absolute -bottom-1 -right-1 rounded-full bg-white p-0.5 border border-slate-200"
                  title="Offline · cached audio">
            <WifiOff className="w-3 h-3 text-amber-500" strokeWidth={2.6} />
          </span>
        )}
      </button>
      {showText && snippet?.text && (
        <span
          data-testid={testid ? `${testid}-text` : `voice-player-${voiceKey}-text`}
          className={`${sz.text} text-slate-800 leading-snug font-medium`}
          lang={snippet.locale || "bn-IN"}
        >
          {snippet.text}
        </span>
      )}
      {autoplayBlocked && (
        <span className="text-[11px] text-amber-600" role="status">
          Tap to play
        </span>
      )}
    </div>
  );
}

export default VoicePlayer;
